import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Menu, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon,
  BellIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useWhiteLabel } from '../../contexts/WhiteLabelContext';
import kyndlyLogoWhite from '../../assets/images/Kyndly-Temp-web-logo-white.png';
import { featureAccess } from '../../config/accessConfig';
import { isFeatureEnabled } from '../../config/environment';
import * as motion from "motion/react-client";
import { usePendingEnrollmentsCount } from '../../hooks/usePendingEnrollmentsCount';
import NotificationBadge from '../ui/NotificationBadge';

// Use a hard-coded version instead
// Add this as a constant near the top of the file
const APP_VERSION = '1.0.0';

// Define animation keyframes for AI badge
const aiAnimationStyles = `
  @keyframes pulse-glow {
    0% {
      box-shadow: 0 0 0 0 rgba(0, 179, 152, 0.7);
      transform: scale(1);
    }
    50% {
      box-shadow: 0 0 6px 3px rgba(0, 179, 152, 0.4);
      transform: scale(1.08);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(0, 179, 152, 0);
      transform: scale(1);
    }
  }
  
  @keyframes sparkle {
    0% {
      opacity: 0.8;
      transform: rotate(0deg);
    }
    50% {
      opacity: 1;
      transform: rotate(5deg);
    }
    100% {
      opacity: 0.8;
      transform: rotate(0deg);
    }
  }
  
  @keyframes float-effect {
    0% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-3px);
    }
    100% {
      transform: translateY(0);
    }
  }
  
  .ai-badge {
    animation: pulse-glow 3.0s infinite ease-in-out;
    position: relative;
    background: linear-gradient(90deg, #00B398 0%, #00A0D2 100%);
    border: 1px solid rgba(255, 255, 255, 0.3);
  }
  
  .ai-sparkle {
    animation: sparkle 1.7s infinite ease-in-out;
    transform-origin: center;
    display: inline-block;
  }
  
  .nav-item-hover:hover svg {
    filter: drop-shadow(0 0 7px rgba(116, 235, 213, 0.8));
    stroke-width: 2px;
    transform: scale(1.2);
  }
  
  .nav-item-hover {
    transition: all 0.3s ease-in-out;
  }
  
  .nav-item-hover:hover {
    transform: translateY(-3px);
    background: linear-gradient(90deg, rgba(0, 179, 152, 0.15), rgba(0, 160, 210, 0.1));
    color: white !important;
  }

  /* Active route styling */
  .nav-item-active {
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
  }

  .nav-item-active svg {
    filter: drop-shadow(0 0 3.5px rgba(116, 235, 213, 0.55));
    stroke-width: 2px;
  }

  /* Company branding footer */
  .company-footer {
    margin-top: auto;
    padding-top: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  .company-footer-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.75rem;
  }

  .company-logo-small {
    height: 2rem;
    margin-bottom: 0.5rem;
  }

  .company-info {
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.6);
    text-align: center;
    line-height: 1.4;
  }
`;

// Types for navigation items
type NavItem = {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  isExternal: boolean;
  aiPowered?: boolean;
  className?: string;
};

// Base navigation (top-level only)
const baseNavigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: DashboardIcon, isExternal: false },
];

// Grouped navigation to mirror nested categories (Employers, Enrollments)
// Use a function to compute navigation based on feature flags
const getGroupedNavigation = (): { group: string; icon: React.ComponentType<any>; children: NavItem[] }[] => {
  const enrollmentsChildren: NavItem[] = [];

  // Only add Kynd Choice if feature is enabled
  if (isFeatureEnabled('kyndChoice')) {
    enrollmentsChildren.push(
      { name: 'Kynd Choice', href: '/kynd-choice', icon: KyndChoiceIcon, isExternal: false }
    );
  }

  // Only add Pending Enrollments if feature is enabled
  if (isFeatureEnabled('pendingEnrollments')) {
    enrollmentsChildren.push(
      { name: 'Pending Enrollments', href: '/enrollments', icon: EnrollmentsIcon, isExternal: false }
    );
  }

  return [
    {
      group: 'Employers',
      icon: EmployersIcon,
      children: [
        { name: 'All Employers', href: '/employers', icon: EmployersIcon, isExternal: false },
        { name: 'Quotes', href: '/quotes', icon: QuotesIcon, isExternal: false },
        { name: 'Sold Cases', href: '/sold-cases', icon: SoldCasesIcon, isExternal: false },
      ],
    },
    {
      group: 'Enrollments',
      icon: EnrollmentsIcon,
      children: enrollmentsChildren,
    },
  ];
};

// Tail navigation shown after grouped sections (no Documents)
const tailNavigation: NavItem[] = [
  { name: 'Knowledge Center', href: '/knowledge-center', icon: KnowledgeCenterIcon, isExternal: false, aiPowered: true },
  { name: 'Admin Panel', href: '/admin-panel', icon: Cog6ToothIcon, isExternal: false, className: 'admin-nav-item' },
];

// User menu items for dropdown
const userNavigation = [
  { name: 'Your Profile', href: '/profile', icon: UserCircleIcon },
  { name: 'Admin Panel', href: '/admin-panel', icon: Cog6ToothIcon },
  { name: 'Sign out', href: '#', icon: ArrowRightOnRectangleIcon },
];

// Icon components
function DashboardIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-7 h-7 transition-all duration-300">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z" />
    </svg>
  );
}

function EmployersIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-7 h-7 transition-all duration-300">
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
    </svg>
  );
}

function QuotesIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-7 h-7 transition-all duration-300">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
    </svg>
  );
}

function SoldCasesIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-7 h-7 transition-all duration-300">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
    </svg>
  );
}

function EnrollmentsIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-7 h-7 transition-all duration-300">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 0 0-1.883 2.542l.857 6a2.25 2.25 0 0 0 2.227 1.932H19.05a2.25 2.25 0 0 0 2.227-1.932l.857-6a2.25 2.25 0 0 0-1.883-2.542m-16.5 0V6A2.25 2.25 0 0 1 6 3.75h3.879a1.5 1.5 0 0 1 1.06.44l2.122 2.12a1.5 1.5 0 0 0 1.06.44H18A2.25 2.25 0 0 1 20.25 9v.776" />
    </svg>
  );
}

function KyndChoiceIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-7 h-7 transition-all duration-300">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z" />
    </svg>
  );
}

function KnowledgeCenterIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-7 h-7 transition-all duration-300">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
    </svg>
  );
}

function DocumentsIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-7 h-7 transition-all duration-300">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 0 0-1.883 2.542l.857 6a2.25 2.25 0 0 0 2.227 1.932H19.05a2.25 2.25 0 0 0 2.227-1.932l.857-6a2.25 2.25 0 0 0-1.883-2.542m-16.5 0V6A2.25 2.25 0 0 1 6 3.75h3.879a1.5 1.5 0 0 1 1.06.44l2.122 2.12a1.5 1.5 0 0 0 1.06.44H18A2.25 2.25 0 0 1 20.25 9v.776" />
    </svg>
  );
}

// Check if we're in development
const isDevelopment = process.env.NODE_ENV === 'development';

export function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hasPlayedLoginAnimation, setHasPlayedLoginAnimation] = useState(() => {
    // Check if animation has already been played in this session
    return sessionStorage.getItem('sidebarAnimationPlayed') === 'true';
  });
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { count: pendingEnrollmentsCount } = usePendingEnrollmentsCount();

  // White-label configuration (always call hook, check feature flag when using values)
  const whiteLabel = useWhiteLabel();
  const whiteLabelEnabled = isFeatureEnabled('whiteLabel');

  // Determine which logo to use based on white-label config
  // Use white Kyndly logo on dark sidebar, or custom white-label logo
  const logoSrc = whiteLabelEnabled && whiteLabel?.config.logoUrl
    ? whiteLabel.config.logoUrl
    : kyndlyLogoWhite;

  const logoAlt = whiteLabelEnabled && whiteLabel?.config.clientName
    ? whiteLabel.config.clientName
    : 'Kyndly';

  // Check if this is the initial login and trigger animation
  useEffect(() => {
    if (user && !hasPlayedLoginAnimation) {
      // Delay setting the flag to allow the animation to play first
      setTimeout(() => {
        setHasPlayedLoginAnimation(true);
        // Store in sessionStorage so it persists across page navigations but resets on new login
        sessionStorage.setItem('sidebarAnimationPlayed', 'true');
      }, 700); // Delay slightly longer than the animation duration (600ms)
    }
  }, [user, hasPlayedLoginAnimation]);

  // Clear animation flag when user logs out
  useEffect(() => {
    if (!user) {
      sessionStorage.removeItem('sidebarAnimationPlayed');
      setHasPlayedLoginAnimation(false);
    }
  }, [user]);
  
  // Helper function to check if a route is active
  const isActiveRoute = (href: string) => {
    if (href === '/dashboard' && location.pathname === '/') {
      return true;
    }
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };
  
  // Handle user logout
  const handleLogout = async () => {
    try {
      await logout();
      // Navigation to login page will be handled by the AuthContext
    } catch (error) {
      console.error('Logout error:', error);
      // Fallback navigation if logout fails
      navigate('/login');
    }
  };

  // Handle navigation for user dropdown items
  const handleUserNavigation = (e: React.MouseEvent, href: string, action?: () => Promise<void>) => {
    e.preventDefault(); // Prevent default behavior
    
    if (action) {
      // Execute the action (e.g., logout)
      action();
    } else {
      // Use the navigate function to change routes
      navigate(href);
    }
    
    // Close mobile sidebar if open
    if (sidebarOpen) {
      setSidebarOpen(false);
    }
  };
  
  const hasRole = (allowedRoles: string[]) => {
    if (!user) return false;
    return allowedRoles.includes(user.role);
  };
  
  // Helper to check if user is admin, tpa_admin, or tpa_user
  const isAdmin = user && (user.role === 'admin' || user.role === 'tpa_admin' || user.role === 'tpa_user');
  
  // Add this right before rendering the Menu.Items
  const filteredUserNavigation = userNavigation.filter(item => 
    !(item.name === 'Admin Panel' && !isAdmin)
  );
  
  return (
    <div className="min-h-screen bg-white dark:bg-dark-bg">
      {/* Inject animation styles */}
      <style dangerouslySetInnerHTML={{ __html: aiAnimationStyles }} />

      {/* Mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button type="button" className="-m-2.5 p-2.5" onClick={() => setSidebarOpen(false)}>
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>

                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-forest px-6 pb-4 shadow-lg">
                  {/* Mobile Logo */}
                  <motion.div
                    className="flex h-16 items-center justify-center"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{
                      delay: 0.2,
                      duration: 0.4,
                      type: "spring",
                      stiffness: 120,
                      damping: 15
                    }}
                  >
                    <Link to="/dashboard" className="p-2 rounded-md flex justify-center items-center w-full">
                      <img
                        className="h-12 w-auto object-contain"
                        src={logoSrc}
                        alt={logoAlt}
                      />
                    </Link>
                  </motion.div>
                  
                  {/* Mobile Navigation */}
                  <motion.nav 
                    className="flex flex-1 flex-col"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{
                      delay: 0.3,
                      duration: 0.3
                    }}
                  >
                    <ul role="list" className="flex flex-1 flex-col gap-y-7">
                      <li>
                        <ul role="list" className="-mx-2 space-y-1">
                          {baseNavigation.map((item, index) => {
                            const featureKey = item.name.toLowerCase().replace(/\s+/g, '') as keyof typeof featureAccess;
                            if (featureAccess[featureKey] && user && !hasRole(featureAccess[featureKey])) return null;

                            return (
                              <motion.li
                                key={item.name}
                                initial={hasPlayedLoginAnimation ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={hasPlayedLoginAnimation ? { duration: 0 } : {
                                  delay: 0.4 + (index * 0.08),
                                  duration: 0.3,
                                  type: "spring",
                                  stiffness: 100,
                                  damping: 20
                                }}
                              >
                                {item.isExternal ? (
                                  <a
                                    href={item.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`group nav-item-hover flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-white hover:text-white hover:bg-mint ${isActiveRoute(item.href) ? 'nav-item-active' : ''}`}
                                  >
                                    <item.icon aria-hidden="true" />
                                    {item.name}
                                  </a>
                                ) : (
                                  <Link
                                    to={item.href}
                                    className={`group nav-item-hover flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-white hover:text-white hover:bg-mint ${isActiveRoute(item.href) ? 'nav-item-active' : ''}`}
                                  >
                                    <item.icon aria-hidden="true" />
                                    {item.name}
                                  </Link>
                                )}
                              </motion.li>
                            );
                          })}
                        </ul>
                        {/* Grouped sections */}
                        <div className="mt-4">
                          {getGroupedNavigation().map((group, gi) => (
                            <div key={group.group} className="mb-3">
                              <div className="px-2 py-1 text-xs font-semibold uppercase tracking-wider text-white/70 flex items-center gap-2">
                                <group.icon />
                                {group.group}
                              </div>
                              <ul role="list" className="-mx-2 mt-1 space-y-1 ml-6">
                                {group.children.map((item, ci) => {
                                  const featureKey = item.name.toLowerCase().replace(/\s+/g, '') as keyof typeof featureAccess;
                                  if (featureAccess[featureKey] && user && !hasRole(featureAccess[featureKey])) return null;
                                  return (
                                    <motion.li
                                      key={item.name}
                                      initial={hasPlayedLoginAnimation ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={hasPlayedLoginAnimation ? { duration: 0 } : {
                                        delay: 0.4 + (gi * 0.1) + (ci * 0.05),
                                        duration: 0.3
                                      }}
                                    >
                                      <Link
                                        to={item.href}
                                        className={`group nav-item-hover flex items-center justify-between rounded-md p-2 text-sm font-semibold leading-6 text-white hover:text-white hover:bg-mint ${isActiveRoute(item.href) ? 'nav-item-active' : ''}`}
                                      >
                                        <div className="flex items-center gap-x-3">
                                          <item.icon aria-hidden="true" />
                                          {item.name}
                                        </div>
                                        {item.name === 'Pending Enrollments' && (
                                          <NotificationBadge count={pendingEnrollmentsCount} />
                                        )}
                                      </Link>
                                    </motion.li>
                                  );
                                })}
                              </ul>
                            </div>
                          ))}
                        </div>
                        {/* Tail navigation */}
                        <ul role="list" className="-mx-2 mt-4 space-y-1">
                          {tailNavigation.map((item, index) => {
                            const featureKey = item.name.toLowerCase().replace(/\s+/g, '') as keyof typeof featureAccess;
                            if (featureAccess[featureKey] && user && !hasRole(featureAccess[featureKey])) return null;

                            // Don't show Admin Panel if user is not an admin
                            if (item.name === 'Admin Panel' && !isAdmin) {
                              return null;
                            }

                            return (
                              <motion.li
                                key={item.name}
                                initial={hasPlayedLoginAnimation ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={hasPlayedLoginAnimation ? { duration: 0 } : {
                                  delay: 0.8 + (index * 0.08),
                                  duration: 0.3,
                                  type: "spring",
                                  stiffness: 100,
                                  damping: 20
                                }}
                              >
                                {item.isExternal ? (
                                  <a
                                    href={item.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`group nav-item-hover flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-white hover:text-white hover:bg-mint ${isActiveRoute(item.href) ? 'nav-item-active' : ''}`}
                                  >
                                    <item.icon aria-hidden="true" />
                                    {item.name}
                                    {item.aiPowered && (
                                      <span className="ml-1 text-xs flex items-center text-white px-1.5 py-0.5 rounded-full ai-badge">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3 h-3 mr-0.5 ai-sparkle">
                                          <path strokeLinecap="round" strokeLinejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" />
                                        </svg>
                                        AI
                                      </span>
                                    )}
                                  </a>
                                ) : (
                                  <Link
                                    to={item.href}
                                    className={`group nav-item-hover flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-white hover:text-white hover:bg-mint ${isActiveRoute(item.href) ? 'nav-item-active' : ''} ${item.className || ''}`}
                                  >
                                    <item.icon aria-hidden="true" />
                                    {item.name}
                                    {item.aiPowered && (
                                      <span className="ml-1 text-xs flex items-center text-white px-1.5 py-0.5 rounded-full ai-badge">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3 h-3 mr-0.5 ai-sparkle">
                                          <path strokeLinecap="round" strokeLinejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" />
                                        </svg>
                                        AI
                                      </span>
                                    )}
                                  </Link>
                                )}
                              </motion.li>
                            );
                          })}
                        </ul>
                      </li>
                    </ul>
                  </motion.nav>
                  
                  {/* Mobile Quick Actions Section */}
                  <motion.div 
                    className="mt-auto mb-4"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{
                      delay: 1.0,
                      duration: 0.4,
                      type: "spring",
                      stiffness: 100,
                      damping: 20,
                      bounce: 0.3
                    }}
                  >
                    <div className="px-2">
                      <motion.h3 
                        className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-3"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: 1.1,
                          duration: 0.3
                        }}
                      >
                        Quick Actions
                      </motion.h3>
                      <div className="space-y-2">
                        {/* New Quote - Role restricted */}
                        {(user?.role === 'admin' || user?.role === 'tpa' || user?.role === 'tpa_admin' || user?.role === 'tpa_user') && (
                          <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{
                              delay: 1.2,
                              duration: 0.3,
                              type: "spring",
                              stiffness: 120,
                              damping: 20
                            }}
                          >
                            <Link
                              to="/quotes/new"
                              className="group flex items-center gap-x-3 rounded-md p-2 text-sm font-medium text-white/90 hover:text-white hover:bg-mint/20 transition-all duration-200"
                            >
                              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-mint/20 group-hover:bg-mint/30 transition-colors">
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                              </div>
                              New Quote
                            </Link>
                          </motion.div>
                        )}
                        
                        {/* Frequently Referenced Documents - Available to all */}
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{
                            delay: (user?.role === 'admin' || user?.role === 'tpa' || user?.role === 'tpa_admin' || user?.role === 'tpa_user') ? 1.4 : 1.2,
                            duration: 0.3,
                            type: "spring",
                            stiffness: 120,
                            damping: 20
                          }}
                        >
                          <Link
                            to="/knowledge-center?from=quick-actions#documents"
                            onClick={() => setSidebarOpen(false)} // Close mobile sidebar
                            className="group flex items-center gap-x-3 rounded-md p-2 text-sm font-medium text-white/90 hover:text-white hover:bg-mint/20 transition-all duration-200"
                          >
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-mint/20 group-hover:bg-mint/30 transition-colors">
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            Frequently Referenced Documents
                          </Link>
                        </motion.div>
                      </div>
                    </div>
                  </motion.div>
                  
                  {/* Mobile Company Footer */}
                  <motion.div 
                    className="company-footer"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      delay: 1.5,
                      duration: 0.4,
                      type: "spring",
                      stiffness: 100,
                      damping: 20
                    }}
                  >
                    <div className="company-footer-content">
                      <img
                        className="company-logo-small object-contain"
                        src={logoSrc}
                        alt={logoAlt}
                      />
                      <div className="company-info">
                        <div>© {new Date().getFullYear()} {logoAlt === 'Kyndly' ? 'Kyndly Technologies' : logoAlt}</div>
                        <div>Version {APP_VERSION}</div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop sidebar */}
      <motion.div 
        className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col shadow-none"
        style={{ 
          overflow: 'hidden',
          willChange: 'transform',
          backfaceVisibility: 'hidden',
          transform: 'translateZ(0)',
          border: 'none',
          borderRight: 'none',
          boxShadow: 'none'
        }}
        initial={hasPlayedLoginAnimation ? { opacity: 1, translateX: 0 } : { opacity: 0, translateX: -288 }}
        animate={{ opacity: 1, translateX: 0 }}
        transition={hasPlayedLoginAnimation ? { duration: 0 } : {
          duration: 0.6,
          ease: "easeOut"
        }}
      >
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-forest dark:bg-dark-surface px-6 pb-4 shadow-none border-none" style={{ border: 'none', borderRight: 'none', boxShadow: 'none' }}>
          {/* Logo */}
          <motion.div
            className="flex h-16 items-center justify-center"
            initial={hasPlayedLoginAnimation ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={hasPlayedLoginAnimation ? { duration: 0 } : {
              delay: 0.4,
              duration: 0.5,
              type: "spring",
              stiffness: 120,
              damping: 15
            }}
          >
            <Link to="/dashboard" className="p-2 rounded-md flex justify-center items-center w-full">
              <img
                className="h-12 w-auto object-contain"
                src={logoSrc}
                alt={logoAlt}
              />
            </Link>
          </motion.div>
          
          {/* Navigation */}
          <motion.nav 
            className="flex flex-1 flex-col"
            initial={hasPlayedLoginAnimation ? { opacity: 1 } : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={hasPlayedLoginAnimation ? { duration: 0 } : {
              delay: 0.5,
              duration: 0.3
            }}
          >
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className="-mx-2 space-y-1">
                  {baseNavigation.map((item, index) => {
                    const featureKey = item.name.toLowerCase().replace(/\s+/g, '') as keyof typeof featureAccess;
                    if (featureAccess[featureKey] && user && !hasRole(featureAccess[featureKey])) return null;

                    return (
                      <motion.li
                        key={item.name}
                        initial={hasPlayedLoginAnimation ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={hasPlayedLoginAnimation ? { duration: 0 } : {
                          delay: 0.6 + (index * 0.1),
                          duration: 0.4,
                          type: "spring",
                          stiffness: 100,
                          damping: 20
                        }}
                      >
                        {item.isExternal ? (
                          <a
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`group nav-item-hover flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-white hover:text-white hover:bg-mint ${isActiveRoute(item.href) ? 'nav-item-active' : ''}`}
                          >
                            <item.icon className="w-7 h-7 transition-all duration-300" aria-hidden="true" />
                            {item.name}
                          </a>
                        ) : (
                          <Link
                            to={item.href}
                            className={`group nav-item-hover flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-white hover:text-white hover:bg-mint ${isActiveRoute(item.href) ? 'nav-item-active' : ''}`}
                          >
                            <item.icon className="w-7 h-7 transition-all duration-300" aria-hidden="true" />
                            {item.name}
                          </Link>
                        )}
                      </motion.li>
                    );
                  })}
                </ul>
                {/* Grouped sections */}
                <div className="mt-4">
                  {getGroupedNavigation().map((group, gi) => (
                    <div key={group.group} className="mb-3">
                      <div className="px-2 py-1 text-xs font-semibold uppercase tracking-wider text-white/70 flex items-center gap-2">
                        <group.icon />
                        {group.group}
                      </div>
                      <ul role="list" className="-mx-2 mt-1 space-y-1 ml-6">
                        {group.children.map((item, ci) => {
                          const featureKey = item.name.toLowerCase().replace(/\s+/g, '') as keyof typeof featureAccess;
                          if (featureAccess[featureKey] && user && !hasRole(featureAccess[featureKey])) return null;
                          return (
                            <motion.li
                              key={item.name}
                              initial={hasPlayedLoginAnimation ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={hasPlayedLoginAnimation ? { duration: 0 } : {
                                delay: 0.6 + (gi * 0.1) + (ci * 0.05),
                                duration: 0.3
                              }}
                            >
                              <Link
                                to={item.href}
                                className={`group nav-item-hover flex items-center justify-between rounded-md p-2 text-sm font-semibold leading-6 text-white hover:text-white hover:bg-mint ${isActiveRoute(item.href) ? 'nav-item-active' : ''}`}
                              >
                                <div className="flex items-center gap-x-3">
                                  <item.icon className="w-7 h-7 transition-all duration-300" aria-hidden="true" />
                                  {item.name}
                                </div>
                                {item.name === 'Pending Enrollments' && (
                                  <NotificationBadge count={pendingEnrollmentsCount} />
                                )}
                              </Link>
                            </motion.li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
                {/* Tail navigation - Desktop */}
                <ul role="list" className="-mx-2 mt-4 space-y-1">
                  {tailNavigation.map((item, index) => {
                    const featureKey = item.name.toLowerCase().replace(/\s+/g, '') as keyof typeof featureAccess;
                    if (featureAccess[featureKey] && user && !hasRole(featureAccess[featureKey])) return null;

                    // Don't show Admin Panel if user is not an admin
                    if (item.name === 'Admin Panel' && !isAdmin) {
                      return null;
                    }

                    return (
                      <motion.li
                        key={item.name}
                        initial={hasPlayedLoginAnimation ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={hasPlayedLoginAnimation ? { duration: 0 } : {
                          delay: 1.2 + (index * 0.1),
                          duration: 0.4,
                          type: "spring",
                          stiffness: 100,
                          damping: 20
                        }}
                      >
                        {item.isExternal ? (
                          <a
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`group nav-item-hover flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-white hover:text-white hover:bg-mint ${isActiveRoute(item.href) ? 'nav-item-active' : ''}`}
                          >
                            <item.icon className="w-7 h-7 transition-all duration-300" aria-hidden="true" />
                            {item.name}
                            {item.aiPowered && (
                              <span className="ml-1 text-xs flex items-center text-white px-1.5 py-0.5 rounded-full ai-badge">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3 h-3 mr-0.5 ai-sparkle">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" />
                                </svg>
                                AI
                              </span>
                            )}
                          </a>
                        ) : (
                          <Link
                            to={item.href}
                            className={`group nav-item-hover flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-white hover:text-white hover:bg-mint ${isActiveRoute(item.href) ? 'nav-item-active' : ''} ${item.className || ''}`}
                          >
                            <item.icon className="w-7 h-7 transition-all duration-300" aria-hidden="true" />
                            {item.name}
                            {item.aiPowered && (
                              <span className="ml-1 text-xs flex items-center text-white px-1.5 py-0.5 rounded-full ai-badge">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3 h-3 mr-0.5 ai-sparkle">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z" />
                                </svg>
                                AI
                              </span>
                            )}
                          </Link>
                        )}
                      </motion.li>
                    );
                  })}
                </ul>
              </li>
              
              {/* Quick Actions Section - Desktop */}
              <motion.li
                initial={hasPlayedLoginAnimation ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={hasPlayedLoginAnimation ? { duration: 0 } : {
                  delay: 1.4,
                  duration: 0.5,
                  type: "spring",
                  stiffness: 100,
                  damping: 20,
                  bounce: 0.3
                }}
              >
                <div className="mb-6">
                  <div className="px-2">
                    <motion.h3 
                      className="text-xs font-semibold text-white/70 uppercase tracking-wider mb-3"
                      initial={hasPlayedLoginAnimation ? { opacity: 1, y: 0 } : { opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={hasPlayedLoginAnimation ? { duration: 0 } : {
                        delay: 1.5,
                        duration: 0.3
                      }}
                    >
                      Quick Actions
                    </motion.h3>
                    <div className="space-y-2">
                      {/* New Quote - Role restricted */}
                      {(user?.role === 'admin' || user?.role === 'tpa' || user?.role === 'tpa_admin' || user?.role === 'tpa_user') && (
                        <motion.div
                          initial={hasPlayedLoginAnimation ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={hasPlayedLoginAnimation ? { duration: 0 } : {
                            delay: 1.6,
                            duration: 0.4,
                            type: "spring",
                            stiffness: 120,
                            damping: 20
                          }}
                        >
                          <Link
                            to="/quotes/new"
                            className="group flex items-center gap-x-3 rounded-md p-2 text-sm font-medium text-white/90 hover:text-white hover:bg-mint/20 transition-all duration-200"
                          >
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-mint/20 group-hover:bg-mint/30 transition-colors">
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                            </div>
                            New Quote
                          </Link>
                        </motion.div>
                      )}
                      
                      {/* Frequently Referenced Documents - Available to all */}
                      <motion.div
                        initial={hasPlayedLoginAnimation ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={hasPlayedLoginAnimation ? { duration: 0 } : {
                          delay: (user?.role === 'admin' || user?.role === 'tpa' || user?.role === 'tpa_admin' || user?.role === 'tpa_user') ? 1.8 : 1.6,
                          duration: 0.4,
                          type: "spring",
                          stiffness: 120,
                          damping: 20
                        }}
                      >
                        <Link
                          to="/knowledge-center?from=quick-actions#documents"
                          className="group flex items-center gap-x-3 rounded-md p-2 text-sm font-medium text-white/90 hover:text-white hover:bg-mint/20 transition-all duration-200"
                        >
                          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-mint/20 group-hover:bg-mint/30 transition-colors">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          Frequently Referenced Documents
                        </Link>
                      </motion.div>
                    </div>
                  </div>
                </div>
              </motion.li>
            </ul>
          </motion.nav>
              
          {/* Company Footer */}
          <motion.div 
            className="company-footer"
            initial={hasPlayedLoginAnimation ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={hasPlayedLoginAnimation ? { duration: 0 } : {
              delay: 1.9,
              duration: 0.4,
              type: "spring",
              stiffness: 100,
              damping: 20
            }}
          >
            <div className="company-footer-content">
              <img
                className="company-logo-small object-contain"
                src={logoSrc}
                alt={logoAlt}
              />
              <div className="company-info">
                <div>© {new Date().getFullYear()} {logoAlt === 'Kyndly' ? 'Kyndly Technologies' : logoAlt}</div>
                <div>Version {APP_VERSION}</div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="lg:pl-72">
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-forest dark:text-neutral-300 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>

          {/* Separator */}
          <div className="h-6 w-px bg-primary-200 dark:bg-dark-border lg:hidden" aria-hidden="true" />

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex-1"></div>
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              {/* Profile dropdown with username */}
              <Menu as="div" className="relative">
                <Menu.Button className="-m-1.5 flex items-center p-1.5">
                  <span className="hidden md:block mr-2 font-medium text-forest dark:text-neutral-100">
                    {user?.name || user?.username || 'User'}
                  </span>
                  <div className="h-8 w-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center text-sm font-semibold text-forest dark:text-primary-100">
                    {user?.name ? user.name.charAt(0).toUpperCase() : (user?.username ? user.username.charAt(0).toUpperCase() : 'U')}
                  </div>
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white dark:bg-dark-surface py-2 shadow-lg ring-1 ring-primary-100 dark:ring-dark-border focus:outline-none">
                    {filteredUserNavigation.map((item) => (
                      <Menu.Item key={item.name}>
                        {({ active }) => (
                          <a
                            href={item.href}
                            onClick={(e) => 
                              item.name === 'Sign out' 
                                ? handleUserNavigation(e, item.href, handleLogout) 
                                : handleUserNavigation(e, item.href)
                            }
                            className={`${active ? 'bg-primary-50 dark:bg-dark-bg' : ''} block px-3 py-1 text-sm leading-6 text-forest dark:text-neutral-200`}
                          >
                            <div className="flex items-center">
                              <item.icon className="h-5 w-5 mr-2 text-primary-500 dark:text-neutral-400" aria-hidden="true" />
                              {item.name}
                            </div>
                          </a>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </div>

        <main className="bg-mint dark:bg-dark-bg">
          <div className="mx-auto px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
} 